import {Alert, Button, Group, Stack, Text, TextInput} from '@mantine/core'
import type {UseFormReturnType} from '@mantine/form'

import {NewWorkFields} from 'src/jobs/new-work-fields'
import {billsEachLine, orderRatePhrase} from 'src/jobs/order-billing'
import type {CreateJobFormValues} from 'src/jobs/types'
import type {ProjectBillingMethod} from 'src/projects/schemas'

import type {AddToOrder} from './job-entry-logic'

type BillingSectionProps = {
    form: UseFormReturnType<CreateJobFormValues>
    order: AddToOrder | null
    billingMethod: ProjectBillingMethod
    orderMethod: ProjectBillingMethod | undefined
    ratesCleared: boolean
    onBillingMethodChange: (method: ProjectBillingMethod) => void
}

export function BillingSection({
    form,
    order,
    billingMethod,
    orderMethod,
    ratesCleared,
    onBillingMethodChange,
}: BillingSectionProps) {
    const extraCharges = form.values.extraCharges ?? []
    const ratesClearedNote =
        ratesCleared && !billsEachLine(billingMethod) ? (
            <Alert color="blue" variant="light" data-testid="line-rates-cleared">
                Line rates cleared. This order is billed {orderRatePhrase(billingMethod)}.
                {order ? null : ' Switching back does not restore them.'}
            </Alert>
        ) : null

    return (
        <>
            <TextInput label="PO number (optional)" {...form.getInputProps('poNumber')} />
            {extraCharges.length > 0 ? (
                <Stack gap={4}>
                    <Text size="sm" fw={500}>
                        Quote charges
                    </Text>
                    {extraCharges.map((charge, index) => (
                        <Group key={`${charge.name}-${index}`} justify="space-between">
                            <Text size="sm">
                                {charge.name}: {charge.quantity} {charge.unit} × $
                                {charge.unitPrice?.toFixed(2)}
                            </Text>
                            <Button
                                size="compact-xs"
                                variant="subtle"
                                color="red"
                                onClick={() =>
                                    form.setFieldValue(
                                        'extraCharges',
                                        extraCharges.filter(
                                            (_, chargeIndex) => chargeIndex !== index,
                                        ),
                                    )
                                }
                            >
                                Remove
                            </Button>
                        </Group>
                    ))}
                </Stack>
            ) : null}
            {order ? (
                <Stack gap={4}>
                    <Text size="sm" c="dimmed">
                        Billed on order {order.orderLabel} billing terms
                        {order.sourceJobId ? ' (same as source job)' : ''}
                        {orderMethod ? `, ${orderRatePhrase(orderMethod)}` : ''}.
                    </Text>
                    {ratesClearedNote}
                </Stack>
            ) : (
                <NewWorkFields
                    form={form}
                    onBillingMethodChange={onBillingMethodChange}
                    methodNote={ratesClearedNote}
                />
            )}
        </>
    )
}
